import { Directive, ElementRef, Renderer2 } from '@angular/core';
import { BaseComponent } from 'sharedFeatures/base';
import { ScrollComponent } from "sharedFeatures/scroll";

import * as moment from 'moment';
import { SaveClosePane } from "sharedFeatures/save-close-pane";
import { NewPlanDto } from 'sharedFeatures/sharedModels';
import { DateUtils } from 'sharedFeatures/utils/date-utils';
import { ItemData } from '../form';
import { NewPlanForm } from './newplan.form';

@Directive({
    standalone: true
})
export abstract class NewPlanInnerForm extends BaseComponent {
    newPlan: NewPlanDto = new NewPlanDto();
    yearComboItems: Array<ItemData> = [];
    actualsYearComboItems: Array<ItemData> = [];
    durationYear: string = "1";
    protected parent!: NewPlanForm;

    monthsComboItems: Array<ItemData> = new DateUtils().monthsComboItems;
    actualsMonthsComboItems: Array<ItemData> = [];
    isActualsStartDateChanged: boolean = false;
    years: Array<ItemData> = [];
    skipOnStartPlanDataChange: boolean = false;


    protected saveClosePane: SaveClosePane | undefined;

    constructor(el: ElementRef, r: Renderer2) {

        super(el, r);

        this.getMonth = this.getMonth.bind(this);
        this.getYear = this.getYear.bind(this);
        this.getYearId = this.getYearId.bind(this);
        this.getActualsYearId = this.getActualsYearId.bind(this);

    }

    public abstract onGoOut(callback: Function): void;

    public abstract reload(callback?: Function): void;

    public abstract getScrollPane(): ScrollComponent;

    public abstract saveClosePanePlaceEl(): Element;

    public isSaveClosePaneHidden(): boolean {

        return !this.saveClosePane || this.saveClosePane.isHidden();

    }

    onCancel(): void {
        const scrollPos = this.getScrollPane().getScrollTop();
        this.reload(() => { this.getScrollPane().setScrollTop(scrollPos); });

        if (this.saveClosePane) {
            this.saveClosePane.close();
            this.saveClosePane = undefined;
        }

        this.r.setStyle(this.saveClosePanePlaceEl(), 'height', '0px')

    }

    calcActualsStartDate(): void {
        let planStartD = <moment.Moment>this.newPlan.planStartDate;
        let planStartYear = planStartD.year();
        let planStartMonth = planStartD.month();

        let actualsStartD = <moment.Moment>this.newPlan.actualsPlanStartDate;
        let actualsStartYear = actualsStartD.year();
        let actualsStartMonth = actualsStartD.month();

        let actualsStartData = DateUtils.ReEvaluateActualsStartDate(
            planStartMonth,
            planStartYear,
            actualsStartYear,
            parseInt(this.durationYear),
            this.parent.authData.maxPlanDuration);

        this.actualsYearComboItems = actualsStartData.years;
        this.actualsMonthsComboItems = actualsStartData.months;
    }

    calcStartDate(refreshCombos: boolean = false): void {
        let actualsStartYear = this.ActualsStartYear;
        let forecastStartYear = this.ForecastStartYear;

        if (actualsStartYear && forecastStartYear) {
            let forecastStartData = DateUtils.ReEvaluateForecastStartDate(
                parseInt(this.actualsMonthString),
                parseInt(actualsStartYear),
                parseInt(forecastStartYear),
                this.parent.authData.maxPlanDuration);

            if (refreshCombos) {
                this.monthsComboItems = forecastStartData.months;
                this.yearComboItems = forecastStartData.years;
                this.years = forecastStartData.forecastYears;

                let newForecastYearIndex = this.yearComboItems.findIndex(s => s.value == forecastStartYear);
                if (newForecastYearIndex != -1) {
                    this.skipOnStartPlanDataChange = true;
                    //this.startYearString = newForecastYearIndex.toString();
                    setTimeout(() => { this.skipOnStartPlanDataChange = false; }, 500);
                }
            }

            let planStartY = parseInt(forecastStartYear);
            let planStartMonth = parseInt(this.monthString);
            let actualsStartY = parseInt(this.ActualsStartYear);
            this.actualsMonthsComboItems = DateUtils.FillActualsMonthArray(
                planStartMonth,
                planStartY,
                actualsStartY,
                parseInt(this.durationYear),
                this.parent.authData.maxPlanDuration / 12
            );
        }
    }

    get ActualsStartYear(): string {
        return moment(this.newPlan.actualsPlanStartDate).year().toString();
    }

    get ForecastStartYear(): string {
        return moment(this.newPlan.planStartDate).year().toString();
    }

    get startYearString(): string {
        let startD = moment(this.newPlan.planStartDate, this.newPlan.dateFormat);
        return startD.year().toString();
    }

    get actualsStartYearString(): string {
        let startD = moment(this.newPlan.actualsPlanStartDate, this.newPlan.dateFormat);
        return startD.year().toString();
    }

    get actualsMonthString(): string {
        let startD = moment(this.newPlan.actualsPlanStartDate, this.newPlan.dateFormat);
        return startD.month().toString();
    }

    get monthString(): string {
        let startD = moment(this.newPlan.planStartDate, this.newPlan.dateFormat);
        return startD.month().toString();
    }

    // Returns month from date string
    getMonth(val: string): string | undefined {

        if (val && val.length > 0 && this.newPlan.dateFormat) {

            const dateFormat = this.newPlan.dateFormat;

            let result = '' + (moment(val, dateFormat).month());
            //console.log(result);
            return result;
        }
    }

    getYear(val: string): string | undefined {

        if (val && val.length > 0 && this.newPlan.dateFormat) {

            const dateFormat = this.newPlan.dateFormat;

            return '' + moment(val, dateFormat).year();
        }
    }

    getYearId(val: string): string | undefined {

        if (val && val.length > 0 && this.newPlan.dateFormat) {

            const dateFormat = this.newPlan.dateFormat;

            let year = '' + moment(val, dateFormat).year();
            return this.yearComboItems.find(y => y.value == year)?.id;
        }
    }

    getActualsYearId(val: string): string | undefined {

        if (val && val.length > 0 && this.newPlan.dateFormat) {

            const dateFormat = this.newPlan.dateFormat;

            let year = '' + moment(val, dateFormat).year();
            return this.actualsYearComboItems.find(y => y.value == year)?.id;
        }
    }

    handleStartDateMonthComboChange(newVal: any): moment.Moment | undefined {
        if (newVal == null) return;

        // newVal can be a string or a number, force convert it to string
        newVal = '' + newVal;

        if (!newVal.length) return;

        // Change month of the startDate
        const startDate = <moment.Moment>this.newPlan.planStartDate;
        let dateDelta = DateUtils.getDateDelta(startDate.clone(), +newVal, 'months');

        // Do nothing if month was not changed
        if (!startDate || '' + (startDate.month()) === newVal) return;

        this.newPlan.planStartDate = startDate.month(+newVal);
        this.newPlan.actualsPlanStartDate = moment(this.newPlan.actualsPlanStartDate).add(dateDelta, 'month');
        this.calcActualsStartDate();

        return this.newPlan.planStartDate;
    }

    handleStartDateYearComboChange(newVal: string): moment.Moment | undefined {
        if (newVal == null) return;

        // Change year of the startDate
        const startDate = <moment.Moment>this.newPlan.planStartDate;
        let year = this.yearComboItems.find(s => s.id == parseInt(newVal))?.value;

        if (year) {
            let dateDelta = DateUtils.getDateDelta(startDate.clone(), +year, 'years');

            // Do nothing if year was not changed
            if (!startDate || '' + startDate.year() === year) return;

            this.newPlan.planStartDate = startDate.year(+year);

            this.newPlan.actualsPlanStartDate = moment(this.newPlan.actualsPlanStartDate).add(dateDelta, 'month');
            this.calcActualsStartDate();
        }
        return this.newPlan.planStartDate;
    }
}
